import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { toastr } from 'react-redux-toastr';
import { Button } from 'react-bootstrap';
import Toggle from 'react-toggle';
import 'react-toggle/style.css';
import { useTranslation } from 'react-i18next';

import { updateUserAccount, saveSettings, getUserNotifications } from '../../../../../shared/api/action/AccountActions';
import AccountNotificationsContainer from './AccountNotificationsContainer';
import IconTooltip from '../../dump/menuComponents/IconTooltip';
import CustomButton from '../../CustomButton';
import { isInternalUser } from '../../helpers/constants';
import { TIMEZONE_LABELS, getUserTimezone, getUseDeviceTz } from '../../helpers/timeDisplaySettings';

const AccountUserContainer = (props) => {
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'accountPage.accountUserContainer' });
	const { user, updateUser, toggleLoader } = props;
	const userRole = user.roles[0];

	const [data, setData] = useState({ first_name: null, last_name: null })
	const [errMessage, setErrMessage] = useState({ first_name: '', last_name: '' })
	const [disabledButton, setDisabledButton] = useState(true)
	const savedData = useRef({})

	useEffect(() => {
		if (['storage_user', 'provisioning', 'partner_view', 'sales_demo', 'custom_user'].includes(userRole)) {
			setData({
				...user,
				time_zone: getUserTimezone(user) || '',
				use_device_tz: getUseDeviceTz(user),
			});
		}
		else {
			getUserSettings()
		}
	}, [])

	const getUserSettings = () => {
		toggleLoader(true);
		getUserNotifications(user.email, userRole)
			.then(res => {
				const resUser = res.data.response.user
				const initData = {
					...resUser,
					time_zone: getUserTimezone(user) || '',
					use_device_tz: !!(resUser.use_device_tz ?? getUseDeviceTz(user)),
				}
				savedData.current = initData
				setData(initData)
				toggleLoader(false);
			})
			.catch(err => {
				toggleLoader(false);
			})
	}

	const fillForm = (e) => {
		const { target } = e;
		const { name } = target;
		const value = target.type === 'checkbox' ? target.checked : target.value;
		const newData = { ...data, ...{ [name]: value } }

		setData(newData);
		setDisabledButton(Object.keys(newData).every(key => newData[key] === savedData.current[key]))
	}

	const checkForm = (data, e) => {
		let error = false;
		const updatedErrMessage = { ...errMessage }

		for (let i in data) {
			if (data[i] === '' && (i === 'first_name' || i === 'last_name')) {
				error = true;
				updatedErrMessage[i] = t('errEnterYour') + i + '!';
			} else if (updatedErrMessage[i]) {
				updatedErrMessage[i] = null;
			}
		}

		setErrMessage(updatedErrMessage);
		return error;
	}

	const sendForm = (e) => {
		e.preventDefault();

		const error = checkForm(data, e);
		if (!error && !disabledButton) {
			toggleLoader(true);

			updateUserAccount(data, userRole)
				.then((res, err) => {
					saveSettings(data)
						.then((res1, err1) => {
							toggleLoader(false);

							const paramUser = res.data.response.user
							const resSettings = res1.data.response.settings
							paramUser.settings.time_zone = resSettings.time_zone
							paramUser.settings.use_device_tz = !!(resSettings.use_device_tz ?? data.use_device_tz)
							updateUser(paramUser);
							toastr.success('', t('userChangesSaved'));
							setDisabledButton(true)
							savedData.current = data
						})
						.catch((error) => {
							toggleLoader(false);
							toastr.error(t('unableUpdateSettings'))
						});
				})
				.catch((error) => {
					toggleLoader(false);
					toastr.error(t('unableUpdateSettings'))
				});
		}
	}

	const { time_zone, use_device_tz, notifications, first_name, last_name, email, speed_unit } = data;

	const readOnly = ['storage_user', 'provisioning', 'partner_view', 'sales_demo', 'custom_user'].indexOf(userRole) > -1;

	return (
		<>
			{!readOnly
				&& (
					<div className='page-subheader'>
						<CustomButton variant='secondary' size='lg' prefix='save-changes' disabled={disabledButton} onClick={sendForm}>
							{t('btnSaveChanges')}
						</CustomButton>
					</div>
				)}

			<main className="main-content-block">
				<section>
					<h3 className='category-title'>{t('titleProfile')}</h3>
					<div className="account-settings two-column-grid with-margin">
						<form onSubmit={sendForm}>
							<h5 className='category-subtitle'>
								{t('subtitleMyInfo')} <label className='subtitle-label'>{email}</label>
							</h5>
							<div className={classnames('two-column-grid', { 'has-error': (!first_name && first_name !== null) || (!last_name && last_name !== null) })}>
								<div>
									<input type="text" readOnly={readOnly} name="first_name" className="vehicle-search" onChange={fillForm} value={first_name || ''} />
									{(!first_name && first_name !== null) && <span className="help-block">{t('enterFirstName')}</span>}
								</div>
								<div>
									<input type="text" readOnly={readOnly} name="last_name" className="vehicle-search" onChange={fillForm} value={last_name || ''} />
									{(!last_name && last_name !== null) && <span className="help-block">{t('enterLastName')}</span>}
								</div>
							</div>
						</form>

						<div className='region-settings'>
							<h5 className='category-subtitle'>
								{t('subtitleRegionSettings')}
							</h5>
							<div>
								<div className='user-timezone'>
									<IconTooltip
										description={t('userTimezoneDescription')}
										id="vehicle-name-tooltip"
									/>
									<label className="user-settings-label">{t('labelTimezone')}</label>
									<select placeholder="select" disabled={readOnly} className="rosco-select timezone-select" name="time_zone" id="time_zone" value={time_zone} onChange={fillForm}>
										<option value="">{t('optionTimezone')}</option>
										{Object.entries(TIMEZONE_LABELS).map(([tzValue, tzLabel]) => (
											<option key={tzValue} value={tzValue}>{tzLabel}</option>
										))}
									</select>
								</div>
								{isInternalUser(userRole) &&
									<div className='user-device-timezone'>
										<label className="user-settings-label" htmlFor="use_device_tz">{t('labelUseDeviceTimezone')}</label>
										<Toggle
											id="use_device_tz"
											name="use_device_tz"
											aria-label={t('labelUseDeviceTimezone')}
											disabled={readOnly}
											checked={!!use_device_tz}
											onChange={fillForm}
										/>
									</div>
								}
								<div className="speed-units-control">
									<IconTooltip
										description={t('descriptionspeedUnit')}
										id="speed-unit-tooltip"
									/>
									<label className="user-settings-label">{t('labelspeedUnit')}</label>
									{!readOnly ?
										<div className='toggle-buttons button-group'>
											<CustomButton
												variant='toggle'
												size='sm'
												active={speed_unit === 'mph'}
												value='mph'
												name='speed_unit'
												style={{ border: "none" }}
												onClick={fillForm}
											>
												MPH
											</CustomButton>
											<CustomButton
												variant='toggle'
												size='sm'
												active={speed_unit === 'kph'}
												value='kph'
												name='speed_unit'
												style={{ border: "none" }}
												onClick={fillForm}
											>
												KPH
											</CustomButton>
										</div> :
										<span>{speed_unit ? speed_unit.toUpperCase() : ''}</span>
									}
								</div>
							</div>
						</div>
					</div>
				</section>
				{!readOnly &&
					<AccountNotificationsContainer
						userEmail={email}
						userRole={userRole}
						notifications={userRole !== 'storage_manager' ? notifications : data}
						fillForm={fillForm}
						getNotifications={getUserSettings}
					/>
				}
			</main>

		</>
	);
}

export default connect(
	state => ({
		user: state.user.user
	}),
	dispatch => ({
		updateUser: (user) => {
			dispatch({ type: 'GET_USER_SUCCESS', payload: user });
		},
		toggleLoader: (show) => {
			dispatch({ type: 'TOGGLE_LOADER', payload: show });
		}
	})
)(AccountUserContainer);
